import { DOCS_SECTIONS, docsPath } from "../pages/docs/docsNav";

export const SITE_ORIGIN = "https://woven.host";
export const SOCIAL_IMAGE = `${SITE_ORIGIN}/social-card.png`;

export interface PublicPage {
  path: string;
  title: string;
  description: string;
}

export interface PageMetadata extends PublicPage {
  canonical: string | null;
  robots: string;
  structuredData: Record<string, unknown>[];
}

const DOCS_DESCRIPTIONS: Record<string, string> = {
  "": "Explore Woven realtime infrastructure: sessions, events, spatial routing, Rust and TypeScript SDKs, self-hosting, and approval-based managed access.",
  quickstart:
    "Request managed Woven access, provision a server, obtain a connection descriptor, and send protocol-valid realtime events with the supported SDKs.",
  "core-concepts":
    "Understand Woven sessions, spaces, channels, ownership, and capacity. Keep application logic separate from product-agnostic realtime routing.",
  sessions:
    "Learn Woven session admission, scoped credentials, server-defined channels, bounded queues, and connection lifecycle for realtime applications.",
  events:
    "Publish opaque realtime payloads with Woven. Understand channel delivery policies, sequencing, payload ceilings, and QUIC datagram limits.",
  "spatial-subspaces":
    "Use bounded 3D Spatial Subspaces and server-defined interest routing to connect relevant participants in Woven realtime applications.",
  "persistent-state":
    "Woven persistence Preview: Ephemeral, process-local Stateful retention, and the Durable journal integration seam. Managed restart recovery is not guaranteed.",
  "inference-network":
    "Explore Woven inference Preview and its separate integration boundary. Managed hosted inference is not a generally available service.",
  authentication:
    "Separate Woven Host account identity from Woven runtime admission. Protect scoped connection credentials and follow approval-based access controls.",
  "sdks/rust":
    "Connect to Woven with the Rust SDK over native QUIC: admission, scoped sessions, protocol-valid publishing, cancellation, and shutdown.",
  "sdks/javascript":
    "Connect browser applications to Woven with the TypeScript SDK over WebTransport: endpoint trust, bigint identifiers, admission, and realtime events.",
  "deploy/self-hosting":
    "Self-host the Apache-2.0 Woven realtime runtime. Configure TLS, scoped credentials, supported transports, and bounded operational limits.",
  "deploy/woven-host":
    "Use Woven Host for approved managed provisioning and monitoring. Clients connect directly to Woven; the control plane is not a realtime data proxy.",
};

export const PUBLIC_PAGES: PublicPage[] = [
  {
    path: "/",
    title: "Woven Host | Open-source realtime infrastructure",
    description:
      "Realtime sessions, events, and spatial routing for games, simulations, and interactive apps. Self-host Woven or request managed access through Woven Host.",
  },
  {
    path: "/pricing",
    title: "Pricing and managed access | Woven Host",
    description:
      "Pro Preview: 2,500 CU for $25/month, priced linearly at $1 per 100 CU; $20/month billed annually. Lite is free after approval. No automated checkout.",
  },
  {
    path: "/network",
    title: "Realtime runtime observations | Woven Host",
    description:
      "View observed Woven runtime activity and capability status from the configured node. Best-effort telemetry is not a global network or uptime guarantee.",
  },
  ...DOCS_SECTIONS.flatMap((section) =>
    section.items.map((item) => {
      const description = DOCS_DESCRIPTIONS[item.slug];
      if (!description) throw new Error(`Missing SEO description for docs route: ${item.slug}`);
      return {
        path: docsPath(item.slug),
        title: `${item.label}${item.status ? ` (${item.status})` : ""} | Woven documentation`,
        description,
      };
    })
  ),
];

export function normalizePath(path: string): string {
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  return pathname.replace(/\/+$/, "") || "/";
}

function privateTitle(path: string): string {
  if (path === "/console") return "woven.host • Overview";
  if (path.startsWith("/console/")) return "woven.host • console";
  if (path === "/sign-in") return "woven.host • sign in";
  if (path === "/sign-up") return "woven.host • sign up";
  if (path === "/sign-up/confirmation") return "woven.host • request confirmation";
  return "woven.host";
}

function structuredDataFor(page: PublicPage): Record<string, unknown>[] {
  const canonical = `${SITE_ORIGIN}${page.path}`;
  const graph: Record<string, unknown>[] = [
    {
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: page.title,
      description: page.description,
      inLanguage: "en",
      isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    },
  ];
  if (page.path === "/") {
    graph.push(
      {
        "@type": "Organization",
        "@id": `${SITE_ORIGIN}/#organization`,
        name: "Woven Host",
        url: `${SITE_ORIGIN}/`,
        logo: `${SITE_ORIGIN}/favicon.svg`,
        sameAs: ["https://github.com/jdharrison/woven"],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_ORIGIN}/#website`,
        name: "Woven Host",
        url: `${SITE_ORIGIN}/`,
        description: page.description,
        inLanguage: "en",
        publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      },
      {
        "@type": "SoftwareApplication",
        name: "Woven",
        applicationCategory: "DeveloperApplication",
        url: "https://github.com/jdharrison/woven",
        license: "https://www.apache.org/licenses/LICENSE-2.0",
        description:
          "Open-source, product-agnostic realtime runtime for sessions, events, and spatial interest routing over QUIC and WebTransport. Application payloads remain opaque to routing.",
      }
    );
  }
  if (page.path.startsWith("/docs")) {
    const breadcrumbs = [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: "Documentation", item: `${SITE_ORIGIN}/docs` },
    ];
    if (page.path !== "/docs") {
      breadcrumbs.push({
        "@type": "ListItem",
        position: 3,
        name: page.title.split(" | ")[0] ?? page.title,
        item: canonical,
      });
    }
    graph.push({ "@type": "BreadcrumbList", itemListElement: breadcrumbs });
  }
  return [{ "@context": "https://schema.org", "@graph": graph }];
}

export function getPageMetadata(path: string): PageMetadata {
  const pathname = normalizePath(path);
  const page = PUBLIC_PAGES.find((entry) => entry.path === pathname);
  if (!page) {
    return {
      path: pathname,
      title: privateTitle(pathname),
      description: "Woven Host account and console workflows require appropriate access.",
      canonical: null,
      robots: "noindex, nofollow",
      structuredData: [],
    };
  }
  return {
    ...page,
    canonical: `${SITE_ORIGIN}${page.path}`,
    robots: "index, follow, max-image-preview:large",
    structuredData: structuredDataFor(page),
  };
}

export function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!
  );
}

export function renderMetadata(metadata: PageMetadata): string {
  const meta = (attribute: "name" | "property", key: string, value: string) =>
    `<meta data-seo ${attribute}="${key}" content="${escapeHtml(value)}" />`;
  const tags = [
    `<title data-seo>${escapeHtml(metadata.title)}</title>`,
    meta("name", "description", metadata.description),
    meta("name", "robots", metadata.robots),
  ];
  if (metadata.canonical) {
    tags.push(
      `<link data-seo rel="canonical" href="${escapeHtml(metadata.canonical)}" />`,
      meta("property", "og:type", "website"),
      meta("property", "og:site_name", "Woven Host"),
      meta("property", "og:locale", "en_US"),
      meta("property", "og:title", metadata.title),
      meta("property", "og:description", metadata.description),
      meta("property", "og:url", metadata.canonical),
      meta("property", "og:image", SOCIAL_IMAGE),
      meta("property", "og:image:type", "image/png"),
      meta("property", "og:image:width", "1200"),
      meta("property", "og:image:height", "630"),
      meta("property", "og:image:alt", "Woven Host — open-source realtime infrastructure"),
      meta("name", "twitter:card", "summary_large_image"),
      meta("name", "twitter:title", metadata.title),
      meta("name", "twitter:description", metadata.description),
      meta("name", "twitter:image", SOCIAL_IMAGE),
      meta("name", "twitter:image:alt", "Woven Host — open-source realtime infrastructure")
    );
  }
  for (const data of metadata.structuredData) {
    tags.push(
      `<script data-seo type="application/ld+json">${JSON.stringify(data).replace(/</g, "\\u003c")}</script>`
    );
  }
  return tags.join("\n");
}

export function applyPageMetadata(path: string): void {
  document.head
    .querySelectorAll(
      '[data-seo], title, meta[name="description"], meta[name="robots"], link[rel="canonical"], meta[property^="og:"], meta[name^="twitter:"]'
    )
    .forEach((element) => element.remove());
  const template = document.createElement("template");
  template.innerHTML = renderMetadata(getPageMetadata(path));
  document.head.append(template.content);
}
